import clsx from 'clsx'
import { useRouter } from 'expo-router'
import { useCallback, useRef, useState, type ReactNode } from 'react'
import { ActivityIndicator, SectionList, View, type ListRenderItem } from 'react-native'

import { Chip } from '@/src/elements/chip/Chip'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { useRoutes } from '@/src/general/utils/routes'
import { getFormattedSectionTitle, type TSection } from '@/src/general/utils/sectionizeInfiniteData'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TConversationApi, TFullMessageApi } from '../../api/types/messageApi.types'
import { useConversationContact } from '../../services/useConversationContact'
import { GET_MESSAGES_LIMIT, useConversationMessagesQuery } from '../../services/useConversationMessagesQuery'
import { useCreateTextMessageMutation } from '../../services/useCreateTextMessageMutation'
import { useDeleteTextMessageMutation } from '../../services/useDeleteTextMessageMutation'
import { BlockedConversationMessage } from './BlockedConversationMessage'
import { ChatMessage } from './ChatMessage'
import { ConversationFooter } from './ConversationFooter'
import { ReportMessageModal } from './ReportMessageModal'

type TSingleConversationProps = {
  conversation: TConversationApi
  userId?: never
}

export const SingleConversation = ({
  conversation: { id, conversationUsers, isBlocked },
}: TSingleConversationProps): ReactNode => {
  const [messageReported, setMessageReported] = useState<TFullMessageApi | undefined>(undefined)
  const sectionListRef = useRef<SectionList<TFullMessageApi, TSection<TFullMessageApi>> | null>(null)
  const router = useRouter()
  const { routes } = useRoutes()
  const { data: me } = useMeQuery()
  const { data, hasPreviousPage, fetchPreviousPage, isFetchingPreviousPage } = useConversationMessagesQuery(id)
  const createTextMessageMutation = useCreateTextMessageMutation({ conversationId: id })
  const { mutateAsync: deleteMessageAsync } = useDeleteTextMessageMutation({ conversationId: id })

  const deleteMessage = async (message: TFullMessageApi) => {
    const shouldRedirect =
      data?.transformedSections.length === 1 &&
      data?.transformedSections[0].data.length === 1 &&
      data?.transformedSections[0].data[0].id === message.id
    try {
      await deleteMessageAsync({ id: message.id })
      if (shouldRedirect) router.navigate(routes.messages())
    } catch {
      // Error is handled by the mutation
    }
  }

  const contact = useConversationContact(conversationUsers)

  const renderItem: ListRenderItem<TFullMessageApi> = ({ item }) => (
    <View className={clsx({ 'pl-10': item.senderId === me?.id, 'pr-10': item.senderId !== me?.id })}>
      <ChatMessage
        key={item.id}
        firstOfBatch
        lastOfBatch
        isSender={item.senderId === me?.id}
        contactName={contact?.name || ''}
        onDeleteMessage={() => deleteMessage(item)}
        onReportMessage={() => setMessageReported(item)}
        {...item}
      />
    </View>
  )

  const scrollToEnd = useCallback(() => {
    sectionListRef.current?.scrollToLocation({ animated: true, sectionIndex: 0, itemIndex: 0 })
  }, [])

  const { t } = useTranslate()

  return (
    <View className="flex-1" testID={TEST_ID.MESSAGES.SINGLE_CONVERSATION}>
      <ReportMessageModal
        conversationId={id}
        messageId={messageReported?.id}
        key={messageReported?.id ?? 'report-message-modal'}
        onClose={() => setMessageReported(undefined)}
        text={messageReported && 'text' in messageReported.payload ? messageReported.payload.text : undefined}
      />
      <SectionList<TFullMessageApi, TSection<TFullMessageApi>>
        ref={sectionListRef}
        testID={TEST_ID.CONVERSATION.MESSAGE.LIST}
        sections={data?.transformedSections || []}
        className="flex-1"
        renderItem={renderItem}
        renderSectionFooter={({ section }) => (
          <Chip
            variant="flat"
            text={getFormattedSectionTitle(section, t)}
            className="self-center bg-default-bg-light"
          />
        )}
        contentContainerClassName="p-4 pt-6"
        ItemSeparatorComponent={() => <Spacer y={4} />}
        SectionSeparatorComponent={() => <Spacer y={4} />}
        onEndReached={() => {
          if (hasPreviousPage && !isFetchingPreviousPage) fetchPreviousPage()
        }}
        onEndReachedThreshold={0.1}
        // +1 fixes a strange bug on Android - maybe it counts the section header too...
        initialNumToRender={GET_MESSAGES_LIMIT + 1}
        ListFooterComponent={
          // The container View must be rendered event if !isFetchingPreviousPage to keep the FlatList height
          <View className="flex h-8 justify-center align-middle">
            {isFetchingPreviousPage && <ActivityIndicator />}
          </View>
        }
        // It is the only way I found to load the list with the scroll at the bottom
        inverted
      />
      {isBlocked ? (
        <BlockedConversationMessage conversationId={id} />
      ) : (
        <ConversationFooter
          onMessageCreated={scrollToEnd}
          newMessageMutation={createTextMessageMutation}
          userId={contact?.id}
          conversationId={id}
        />
      )}
    </View>
  )
}
